import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, CalendarPlus, Printer, Search, ShieldCheck, Phone, XCircle, Check, Clock, Ban, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorCode } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { usePageMeta } from "@/lib/seo";
import { fmtDateTime } from "@/lib/format";
import { downloadIcs } from "@/lib/booking";
import { BookingDetails } from "./Booking";

const STATUS_LABEL = {
  pending: { nn: "Motteken", en: "Received", cls: "bg-[hsl(34_35%_55%)] text-primary" },
  confirmed: { nn: "Stadfesta", en: "Confirmed", cls: "bg-[hsl(168_28%_34%)] text-white" },
  declined: { nn: "Avslått", en: "Declined", cls: "bg-destructive text-destructive-foreground" },
  cancelled: { nn: "Avbestilt", en: "Cancelled", cls: "bg-muted text-muted-foreground" },
};

const Timeline = ({ b }) => {
  const { lang, tx } = useLang();
  const hist = b.status_history || [];
  const icon = (s) => (s === "confirmed" ? Check : s === "declined" ? Ban : s === "cancelled" ? XCircle : Clock);
  const label = (h) => {
    const base = lang === "en" ? STATUS_LABEL[h.status]?.en : STATUS_LABEL[h.status]?.nn;
    const by = h.by === "guest" ? tx(" av deg", " by you") : h.by === "admin" ? tx(" av hotellet", " by the hotel") : h.by === "auto" ? tx(" automatisk", " automatically") : "";
    return `${base}${by}${h.late ? tx(" (etter fristen)", " (after deadline)") : ""}`;
  };
  return (
    <ol className="relative space-y-5 border-l border-border pl-6" data-testid="my-booking-status-timeline">
      {hist.map((h, i) => {
        const I = icon(h.status);
        const last = i === hist.length - 1;
        return (
          <li key={i} className="relative">
            <span className={`absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full border-2 border-background ${last ? "bg-primary text-primary-foreground" : "bg-accent text-primary"}`}><I className="h-3 w-3" /></span>
            <p className={`text-sm ${last ? "font-semibold text-foreground" : "text-foreground/80"}`}>{label(h)}</p>
            <p className="text-xs text-muted-foreground">{fmtDateTime(h.at, lang)}</p>
          </li>
        );
      })}
      {b.status === "pending" && (
        <li className="relative">
          <span className="absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full border-2 border-dashed border-border bg-background" />
          <p className="text-sm text-muted-foreground">{tx("Ventar på stadfesting frå hotellet", "Awaiting confirmation from the hotel")}</p>
        </li>
      )}
    </ol>
  );
};

export default function MyBooking() {
  const [sp] = useSearchParams();
  const { lang, tx } = useLang();
  const { data: content } = useContent();
  usePageMeta(tx("Mi bestilling", "My booking"), tx("Sjå, endre eller avbestill opphaldet ditt på Fitjar Fjordhotell.", "View, change or cancel your stay at Fitjar Fjordhotell."));
  const byId = Object.fromEntries((content?.room_types || []).map((r) => [r.id, r]));
  const [ref, setRef] = useState(sp.get("ref") || "");
  const [email, setEmail] = useState(sp.get("email") || "");
  const [booking, setBooking] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const lookup = useMutation({
    mutationFn: async () => (await api.post("/bookings/lookup", { ref: ref.trim().toUpperCase(), email: email.trim() })).data,
    onSuccess: (b) => setBooking(b),
    onError: (err) => { setBooking(null); toast.error(errorText(errorCode(err), lang)); },
  });
  const cancel = useMutation({
    mutationFn: async () => (await api.post("/bookings/cancel", { ref: booking.ref, email: booking.guest.email })).data,
    onSuccess: (b) => { setBooking(b); setConfirmOpen(false); toast.success(tx("Bestillinga er avbestilt. Me har sendt deg ei stadfesting.", "Your booking is cancelled. We've emailed you a confirmation.")); },
    onError: (err) => toast.error(errorText(errorCode(err), lang)),
  });

  useEffect(() => {
    if (sp.get("ref") && sp.get("email") && !booking && !lookup.isPending) lookup.mutate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const st = booking ? STATUS_LABEL[booking.status] || STATUS_LABEL.pending : null;
  const roomNames = Object.fromEntries(Object.values(byId).map((r) => [r.id, r.name?.[lang] || r.name?.nn]));

  return (
    <div className="mx-auto max-w-5xl px-4 pb-24 pt-10 sm:px-6 lg:px-8 lg:pt-16" data-testid="my-booking-page">
      <div className="max-w-2xl">
        <p className="kicker mb-4">{tx("Mi bestilling", "My booking")}</p>
        <h1 className="font-display text-4xl font-light leading-tight text-primary sm:text-5xl">{tx("Finn opphaldet ditt.", "Find your stay.")}</h1>
        <p className="mt-4 text-muted-foreground">{tx("Skriv inn bookingreferansen (FFH-…) frå e-posten og e-postadressa du bestilte med.", "Enter the booking reference (FFH-…) from your email and the email address you booked with.")}</p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); lookup.mutate(); }} className="mt-8 grid gap-4 rounded-[1.5rem] border border-border bg-card p-5 sm:grid-cols-[1fr_1.4fr_auto] sm:items-end sm:p-6 no-print" data-testid="my-booking-lookup-form">
        <div className="space-y-1.5">
          <Label htmlFor="lookup-ref">{tx("Bookingreferanse", "Booking reference")}</Label>
          <Input id="lookup-ref" value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} placeholder="FFH-XXXXX" className="h-12 rounded-xl bg-background font-mono uppercase" required data-testid="my-booking-ref-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lookup-email">{tx("E-post", "Email")}</Label>
          <Input id="lookup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl bg-background" required data-testid="my-booking-email-input" />
        </div>
        <Button type="submit" size="lg" disabled={lookup.isPending} data-testid="my-booking-lookup-submit"><Search className="h-4 w-4" /> {lookup.isPending ? tx("Søkjer …", "Searching …") : tx("Finn bestilling", "Find booking")}</Button>
      </form>

      {lookup.isError && !booking && (
        <div className="mt-6 rounded-2xl bg-secondary p-5 text-sm" data-testid="my-booking-not-found">
          <p className="font-semibold text-primary">{errorText(errorCode(lookup.error), lang)}</p>
          <p className="mt-1 text-muted-foreground">{tx("Sjekk at referansen og e-posten er dei same som i stadfestinga. Finn du ho ikkje? Ring oss på 53 45 77 00.", "Check that the reference and email match your confirmation. Can't find it? Call us on +47 53 45 77 00.")}</p>
        </div>
      )}

      {booking && (
        <div className="mt-10 grid gap-8 lg:grid-cols-12" data-testid="my-booking-result">
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{tx("Bookingreferanse", "Booking reference")}</p>
                <p className="font-mono text-3xl font-semibold text-primary" data-testid="my-booking-reference">{booking.ref}</p>
              </div>
              <Badge className={`rounded-full px-3 py-1 text-xs font-semibold ${st.cls} hover:${st.cls}`} data-testid="my-booking-status">{lang === "en" ? st.en : st.nn}</Badge>
            </div>
            {booking.admin_message && (
              <div className="mt-5 rounded-2xl border border-border bg-card p-4" data-testid="my-booking-admin-message">
                <p className="kicker mb-1">{tx("Melding frå hotellet", "Message from the hotel")}</p>
                <p className="text-sm text-foreground">{booking.admin_message}</p>
              </div>
            )}
            <div className="mt-6 rounded-[1.75rem] border border-border bg-card p-5 sm:p-7">
              <BookingDetails b={booking} byId={byId} lang={lang} />
            </div>
          </div>
          <aside className="space-y-6 lg:col-span-4">
            <div className="rounded-[1.5rem] border border-border bg-card p-5">
              <p className="kicker mb-4">{tx("Status", "Status")}</p>
              <Timeline b={booking} />
            </div>
            <div className="rounded-[1.5rem] border border-border bg-card p-5 no-print">
              <p className="kicker mb-4">{tx("Handlingar", "Actions")}</p>
              <div className="grid gap-2">
                <Button variant="outline" className="h-11 justify-start" onClick={() => downloadIcs(booking, roomNames, lang)} data-testid="my-booking-ics"><CalendarPlus className="h-4 w-4" /> {tx("Legg i kalender", "Add to calendar")}</Button>
                <Button variant="outline" className="h-11 justify-start" onClick={() => window.print()} data-testid="my-booking-print"><Printer className="h-4 w-4" /> {tx("Skriv ut", "Print")}</Button>
                {booking.can_cancel ? (
                  <Button variant="ghost" className="h-11 justify-start text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setConfirmOpen(true)} data-testid="my-booking-cancel-button"><XCircle className="h-4 w-4" /> {tx("Avbestill opphaldet", "Cancel the stay")}</Button>
                ) : null}
              </div>
              <p className="mt-4 flex gap-2 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4 shrink-0 text-moss" />
                {booking.can_cancel
                  ? booking.can_cancel_free
                    ? tx(`Gratis avbestilling fram til ${booking.cancel_deadline_display?.nn}.`, `Free cancellation until ${booking.cancel_deadline_display?.en}.`)
                    : tx("Fristen for gratis avbestilling er ute. Avbestiller du no, vert opphaldet fakturert.", "The free cancellation deadline has passed. Cancelling now will be charged for the stay.")
                  : tx("Denne bestillinga kan ikkje endrast her.", "This booking can't be changed here.")}
              </p>
              <p className="mt-3 text-xs text-muted-foreground">{tx("Vil du endre datoar eller rom? ", "Want to change dates or rooms? ")}<a href="tel:+4753457700" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"><Phone className="h-3 w-3" /> 53 45 77 00</a> · <a href="mailto:post@fitjar-fjordhotell.no" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"><Mail className="h-3 w-3" /> {tx("e-post", "email")}</a></p>
            </div>
          </aside>
        </div>
      )}

      {!booking && !lookup.isError && (
        <div className="mt-12 grid gap-4 sm:grid-cols-2 no-print">
          <div className="rounded-2xl bg-secondary p-5">
            <p className="font-display text-xl text-primary">{tx("Har du ikkje bestilt enno?", "Haven't booked yet?")}</p>
            <Button asChild className="mt-4" data-testid="my-booking-book-cta"><Link to="/bestill">{tx("Finn ledige rom", "Find available rooms")} <ArrowRight className="h-4 w-4" /></Link></Button>
          </div>
          <div className="rounded-2xl border border-border p-5 text-sm text-muted-foreground">
            <p className="font-semibold text-primary">{tx("Avbestillingsvilkår", "Cancellation terms")}</p>
            <p className="mt-2">{content?.settings?.cancellation?.[lang] || content?.settings?.cancellation?.nn}</p>
          </div>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-3xl border-border bg-background" data-testid="my-booking-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-normal text-primary">{tx("Avbestille opphaldet?", "Cancel the stay?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {booking?.can_cancel_free
                ? tx("Du er innanfor fristen – avbestillinga er gratis. Me sender ei stadfesting på e-post.", "You're within the deadline – cancellation is free. We'll email a confirmation.")
                : tx("Fristen for gratis avbestilling er ute. Opphaldet vert fakturert i tråd med vilkåra. Ring oss gjerne først på 53 45 77 00.", "The free cancellation deadline has passed. The stay will be charged per the terms. Feel free to call us first on +47 53 45 77 00.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="my-booking-cancel-dismiss">{tx("Behald bestillinga", "Keep booking")}</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={(e) => { e.preventDefault(); cancel.mutate(); }} disabled={cancel.isPending} data-testid="my-booking-cancel-confirm">{cancel.isPending ? tx("Avbestiller …", "Cancelling …") : tx("Ja, avbestill", "Yes, cancel")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
